import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { format } from 'date-fns';
import MainLayout from '../components/MainLayout';
import { uiConfig } from '../config';
import { useAppSelector } from '../hooks/redux';
import { useRoleAccess } from '../hooks/useRoleAccess';
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

interface ConversationSession {
  _id: string;
  sessionId: string;
  sessionName: string;
  userId: string;
  userEmail: string;
  callStartTime: string;
  callEndTime: string;
  callDuration: number;
  metadata: {
    roomName?: string;
    clientId?: string;
    source?: 'calendar' | 'manual' | 'standalone_desktop' | string;
    platform?: string;
  };
  statistics: {
    totalTranscripts: number;
    totalObjections: number;
    totalFAQs: number;
    totalNewInquiries: number;
    totalLandmineDetections?: number;
    totalCompetitorMentions?: number;
  };
  status: string;
  createdAt: string;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface SessionsApiResponse {
  success: boolean;
  data?: ConversationSession[];
  pagination?: PaginationInfo;
  message?: string;
  error?: string;
}

interface UserInfo {
  userId: string;
  userEmail: string;
  name?: string;
}

const Sessions: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin } = useRoleAccess();
  const { user } = useAppSelector((state) => state.auth);
  const [sessions, setSessions] = useState<ConversationSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: uiConfig.defaultPageSize,
    total: 0,
    pages: 0
  });
  const [pageSize, setPageSize] = useState(uiConfig.defaultPageSize);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [goToPageInput, setGoToPageInput] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string>('all');
  const [users, setUsers] = useState<UserInfo[]>([]);

  useEffect(() => {
    fetchSessions();
    if (isAdmin) {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pageSize, filterStatus, selectedUserId, isAdmin]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pageSize.toString(),
        sortBy: 'callStartTime',
        sortOrder: 'desc',
      });

      if (filterStatus !== 'all') {
        params.append('status', filterStatus);
      }

      // For admin users with specific user selected, or regular users
      if (isAdmin && selectedUserId !== 'all') {
        params.append('userId', selectedUserId);
      } else if (!isAdmin && user?.uid) {
        params.append('userId', user.uid);
      }

      const response = await apiService.get(`/admin/sessions?${params}`) as SessionsApiResponse;
      
      console.log('Sessions API Response:', response); // Debug log
      
      // The response is the full response object from backend
      if (response?.success) {
        setSessions(response.data || []);
        setPagination(response.pagination || {
          page: 1,
          limit: uiConfig.defaultPageSize,
          total: 0,
          pages: 0
        });
      }
    } catch (err: any) {
      console.error('Failed to fetch sessions:', err);
      // Check if it's an authentication error
      if (err?.error === 'AUTHENTICATION_FAILED' || err?.error === 'NOT_AUTHENTICATED') {
        setError('Your session has expired. Please log in again.');
      } else {
        setError(err.message || 'Failed to load sessions');
      }
    } finally {
      setLoading(false);
    }
  };

  // Populate the filter from ALL org users (any role, active or not) — not just
  // those who happen to have sessions — since admins also run calls.
  const fetchUsers = async () => {
    try {
      const response: any = await apiService.get('/admin/users', { limit: '500' });
      const list: any[] = response?.data?.users ?? response?.data ?? [];
      const mapped: UserInfo[] = list
        .filter((u) => u && (u.firebaseUid || u.uid) && u.email)
        .map((u) => ({
          userId: u.firebaseUid || u.uid,
          userEmail: u.email,
          name: u.name,
        }))
        .sort((a, b) =>
          (a.name || a.userEmail).localeCompare(b.name || b.userEmail, undefined, {
            sensitivity: 'base',
          })
        );
      setUsers(mapped);
    } catch (err: any) {
      console.error('Failed to fetch users:', err);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPagination(prev => ({ ...prev, page: 1, limit: newSize })); // Reset to page 1 when changing page size
  };

  const handleUserChange = (event: SelectChangeEvent<string>) => {
    setSelectedUserId(event.target.value);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1 when changing user
  };

  const handleGoToPage = () => {
    const pageNum = parseInt(goToPageInput);
    if (pageNum && pageNum >= 1 && pageNum <= pagination.pages) {
      handlePageChange(pageNum);
      setGoToPageInput('');
    }
  };

  // Generate page numbers with ellipsis
  const generatePageNumbers = () => {
    const pages = [];
    const totalPages = pagination.pages;
    const currentPage = pagination.page;
    const maxVisible = 7; // Maximum number of page buttons to show

    if (totalPages <= maxVisible) {
      // Show all pages if total is less than max
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push('...'); // Ellipsis before current page group
      }

      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }

      if (currentPage < totalPages - 2) {
        pages.push('...'); // Ellipsis after current page group
      }

      // Always show last page
      if (!pages.includes(totalPages)) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    } else {
      return `${secs}s`;
    }
  };

  const viewSessionDetails = (sessionId: string) => {
    navigate(`/sessions/${sessionId}`);
  };

  // Get session type label and styling based on metadata.source
  const getSessionType = (source?: string, platform?: string): { label: string; className: string; icon: string; platformLabel?: string } => {
    switch (source) {
      case 'calendar':
        return {
          label: 'Calendar Bot',
          className: 'bg-purple-100 text-purple-800',
          icon: '📅'
        };
      case 'manual':
        return {
          label: 'Manual Bot',
          className: 'bg-blue-100 text-blue-800',
          icon: '🤖'
        };
      case 'standalone_desktop':
        return {
          label: 'Desktop App',
          className: 'bg-green-100 text-green-800',
          icon: '🖥️',
          platformLabel: platform === 'macos' ? 'Apple' : platform === 'windows' ? 'Windows' : undefined
        };
      default:
        return {
          label: 'Unknown',
          className: 'bg-gray-100 text-gray-800',
          icon: '❓'
        };
    }
  };

  return (
    <MainLayout title="Conversation Sessions">
      <div className="p-2">
        <div className="mb-3">
          <h1 className="text-2xl font-bold text-gray-900">Conversation Sessions</h1>
          <p className="text-gray-600 mt-1">
            {isAdmin
              ? selectedUserId === 'all'
                ? 'View and manage all recorded conversation sessions'
                : `View sessions for ${users.find(u => u.userId === selectedUserId)?.userEmail || 'selected user'}`
              : 'View your recorded conversation sessions'}
          </p>
        </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow mb-6 p-4">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-end">
          <div className="flex gap-2 items-center">
            {/* Admin User Dropdown */}
            {isAdmin && (
              <FormControl size="small" sx={{ minWidth: 250 }}>
                <InputLabel>View Sessions For</InputLabel>
                <Select
                  value={selectedUserId}
                  label="View Sessions For"
                  onChange={handleUserChange}
                >
                  <MenuItem value="all">Across Organization</MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.userId} value={user.userId}>
                      {user.name ? `${user.name} (${user.userEmail})` : user.userEmail}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <select
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="in_progress">In Progress</option>
              <option value="failed">Failed</option>
            </select>

            <Tooltip title="Refresh">
              <IconButton
                onClick={fetchSessions}
                color="primary"
                size="medium"
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Sessions Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading && sessions.length === 0 ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">Loading sessions...</p>
          </div>
        ) : (
          <div className="relative">
            {/* Loading Overlay - shows when refetching data */}
            {loading && (
              <div className="absolute inset-0 bg-white bg-opacity-75 z-10 flex items-center justify-center rounded-lg">
                <div className="flex flex-col items-center">
                  <CircularProgress />
                  <p className="mt-2 text-gray-600">Updating sessions...</p>
                </div>
              </div>
            )}

            <div>
              <table className="w-full table-fixed divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '25%' }}>
                  Session Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '15%' }}>
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '15%' }}>
                  Session Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '20%' }}>
                  Statistics
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{ width: '10%' }}>
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sessions.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    No sessions found
                  </td>
                </tr>
              ) : (
                sessions.map((session) => (
                  <tr
                    key={session._id}
                    onClick={() => viewSessionDetails(session.sessionId)}
                    className="hover:bg-blue-50 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900 break-words">
                          {session.sessionName}
                        </div>
                        <div className="text-xs text-gray-500">
                          {
                            session?.callStartTime ? 
                              format(new Date(session?.callStartTime), 'MMM dd, yyyy HH:mm')
                            :
                            ''
                          }
                        
                        </div>
                        <div className="text-xs text-gray-400 font-mono break-all">
                          {session.sessionId.substring(0, 20)}...
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 break-words">{session.userEmail}</div>
                    </td>
                    <td className="px-6 py-4">
                      {(() => {
                        const sessionType = getSessionType(session.metadata?.source, session.metadata?.platform);
                        return (
                          <div className="inline-flex flex-col items-center gap-1">
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full ${sessionType.className}`}>
                              <span>{sessionType.label}</span>
                            </span>
                            {sessionType.platformLabel && (
                              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                                {sessionType.platformLabel}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-xs space-y-1">
                        <div className="flex items-center gap-1">
                          <span className="text-gray-500">📝</span>
                          <span>{session.statistics?.totalTranscripts || 0} transcripts</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-gray-500">❓</span>
                          <span>{session.statistics?.totalObjections || 0} objections</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-gray-500">📚</span>
                          <span>{session.statistics?.totalFAQs || 0} FAQs</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-gray-500">💡</span>
                          <span>{session.statistics?.totalNewInquiries || 0} new objection/faq</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-gray-500">⚠️</span>
                          <span>{session.statistics?.totalLandmineDetections || 0} landmines</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-gray-500">🏢</span>
                          <span>{session.statistics?.totalCompetitorMentions || 0} competitors</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        session.status === 'completed'
                          ? 'bg-green-100 text-green-800'
                          : session.status === 'in_progress'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {session.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
              </table>
            </div>

            {/* Pagination */}
            {(pagination.total > 0) && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="text-sm text-gray-700">
                      Showing {((pagination.page - 1) * pageSize) + 1} to{' '}
                      {Math.min(pagination.page * pageSize, pagination.total)} of{' '}
                      {pagination.total} results
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-sm text-gray-700">Per page:</label>
                      <select
                        value={pageSize}
                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                        className="px-2 py-1 border border-gray-300 rounded-md bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {/* First Page Button */}
                    <button
                      onClick={() => handlePageChange(1)}
                      disabled={pagination.page === 1}
                      className="px-2 py-1 border border-gray-300 rounded-md bg-white text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      title="First page"
                    >
                      ««
                    </button>

                    {/* Previous Button */}
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="px-3 py-1 border border-gray-300 rounded-md bg-white text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>

                    {/* Page Numbers */}
                    {generatePageNumbers().map((pageNum, index) => {
                      if (pageNum === '...') {
                        return (
                          <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                            ...
                          </span>
                        );
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum as number)}
                          className={`px-3 py-1 rounded-md text-sm transition-colors ${
                            pageNum === pagination.page
                              ? 'bg-blue-600 text-white'
                              : 'border border-gray-300 bg-white hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    {/* Next Button */}
                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.pages}
                      className="px-3 py-1 border border-gray-300 rounded-md bg-white text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>

                    {/* Last Page Button */}
                    <button
                      onClick={() => handlePageChange(pagination.pages)}
                      disabled={pagination.page === pagination.pages}
                      className="px-2 py-1 border border-gray-300 rounded-md bg-white text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      title="Last page"
                    >
                      »»
                    </button>

                    {/* Go to Page Input */}
                    {pagination.pages > 10 && (
                      <div className="flex items-center gap-1 ml-4">
                        <span className="text-sm text-gray-600">Go to:</span>
                        <input
                          type="number"
                          min="1"
                          max={pagination.pages}
                          value={goToPageInput}
                          onChange={(e) => setGoToPageInput(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleGoToPage();
                            }
                          }}
                          className="w-16 px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="#"
                        />
                        <button
                          onClick={handleGoToPage}
                          className="px-2 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 transition-colors"
                        >
                          Go
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      </div>
    </MainLayout>
  );
};

export default Sessions;